import { Link, useSearchParams } from 'react-router-dom';
import React, { useMemo } from 'react';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { IntegrationV2Properties } from '@/gql/graphql';
import { TrackingDestinationCard } from './tracking-destination-card';
import { SOURCES } from './constants';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';

const DESTINATION_STATUS = Object.freeze([
  { value: 'ALL', label: 'All', status: 'all' },
  { value: 'CONNECTED', label: 'Active', status: 'active' },
  { value: 'DISCONNECTED', label: 'Inactive', status: 'inactive' },
  { value: 'DRAFT', label: 'Draft', status: 'draft' },
  { value: 'ERROR', label: 'Error', status: 'error' }
]);

const TABS_CONFIG = DESTINATION_STATUS.map((status) => ({
  value: status.status,
  label: status.label,
  statusValue: status.value
}));

interface TrackingCardsComponentProps {
  destinations: IntegrationV2Properties[] | undefined;
}

export function TrackingCardsComponent({ destinations }: TrackingCardsComponentProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentScreenParam = searchParams.get('screen');
  const selectedTab = TABS_CONFIG.find((t) => t.value === currentScreenParam)?.value ?? 'all';

  const isMobile = useIsMobile();

  const handleTabChange = (statusValue: string) => {
    const tab = TABS_CONFIG.find((t) => t.statusValue === statusValue);
    const screenParam = tab ? tab.value : statusValue.toLowerCase();
    setSearchParams({ screen: screenParam });
  };

  const selectedTabConfig = TABS_CONFIG.find((t) => t.value === selectedTab);
  const filterStatus = selectedTabConfig?.statusValue;

  const filteredIntegrationCards = useMemo(() => {
    return destinations
      ?.filter((integration) => {
        if (selectedTab === 'all') {
          return true;
        }
        return integration.status === filterStatus;
      })
      .map((integration) => (
        <TrackingDestinationCard key={integration.id} integration={integration} />
      ));
  }, [destinations, selectedTab, filterStatus]);

  return (
    <>
      <div className="mb-6 mt-16">
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Data Destinations</h2>
            <Button type="button" asChild>
              <Link to=".?new">
                <Plus size={16} className="mr-0 md:mr-2" />
                <span className="hidden md:inline">Add Destination</span>
              </Link>
            </Button>
          </div>
        </div>
        <Tabs
          value={selectedTab}
          onValueChange={(newValue) => {
            const tab = TABS_CONFIG.find((t) => t.value === newValue);
            if (tab) {
              handleTabChange(tab.statusValue);
            }
          }}
        >
          {isMobile ? (
            <Select value={selectedTab} onValueChange={handleTabChange}>
              <SelectTrigger className="w-full">
                {TABS_CONFIG.find((tab) => tab.value === selectedTab)?.label ?? 'Select'}
              </SelectTrigger>
              <SelectContent>
                {TABS_CONFIG.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value}>
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <TabsList className="rounded-xl flex-wrap">
              {TABS_CONFIG.map((tab) => (
                <TabsTrigger key={tab.statusValue} value={tab.value} className="rounded-lg px-8">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
        </Tabs>

        {filteredIntegrationCards && filteredIntegrationCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
            {filteredIntegrationCards}
          </div>
        )}
        {filteredIntegrationCards && filteredIntegrationCards.length === 0 && (
          <div className="mt-6">
            <p>No destinations found</p>
          </div>
        )}
      </div>
      <div>
        <h2 className="text-xl font-semibold">Sources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6 mb-10">
          {SOURCES.map((integration) => (
            <TrackingDestinationCard key={integration.id} integration={integration} />
          ))}
        </div>
      </div>
    </>
  );
}
